import { Alert, Button, Card, H1, H3, Text } from '@payaca/ambrosia';
import qs from 'qs';
import { FC, useMemo } from 'react';

import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';

import UntitledIcon from '@payaca/untitled-icons';

import { useApproveOAuth2, useDenyOAuth2 } from '@/api/hooks/oauth2';
import useGetMe from '@/api/queries/me/useGetMe';

type Props = {
  location?: any;
  history?: any;
};

const OAuth2AuthorizePage: FC<Props> = ({ location, history }) => {
  const queryParams = useMemo(() => {
    return qs.parse(location.search, { ignoreQueryPrefix: true });
  }, [location.search]);

  // Map snake_case OAuth2 parameters to camelCase
  const clientId = queryParams.client_id as string;
  const redirectUri = queryParams.redirect_uri as string;
  const responseType = queryParams.response_type as string;
  const state = queryParams.state as string;
  const scope = queryParams.scope as string;
  const codeChallenge = queryParams.code_challenge as string;
  const codeChallengeMethod = queryParams.code_challenge_method as string;
  const resource = queryParams.resource as string;

  const approveMutation = useApproveOAuth2();
  const denyMutation = useDenyOAuth2();
  const { data: me, isLoading: isLoadingMe } = useGetMe();

  const handleApprove = async () => {
    if (!clientId || !redirectUri) return;

    try {
      const result = await approveMutation.mutateAsync({
        clientId,
        redirectUri,
        responseType: responseType || 'code',
        state,
        scope,
        codeChallenge,
        codeChallengeMethod,
        resource,
      });

      if (result.redirectUrl) {
        // These codes only work once, so we replace the current history entry
        // to prevent the user from going back to it.
        window.location.replace(result.redirectUrl);
      }
    } catch (error) {
      console.error('Failed to approve authorization:', error);
    }
  };

  const handleDeny = async () => {
    if (!redirectUri) return;

    try {
      const result = await denyMutation.mutateAsync({
        redirectUri,
        state,
      });

      if (result.redirectUrl) {
        // These codes only work once, so we replace the current history entry
        // to prevent the user from going back to it.
        window.location.replace(result.redirectUrl);
      }
    } catch (error) {
      console.error('Failed to deny authorization:', error);
    }
  };

  const scopeList = useMemo(() => {
    if (!scope) return [];
    return scope.split(' ').filter(Boolean);
  }, [scope]);

  if (isLoadingMe) {
    return <LoaderOverlay />;
  }

  if (!clientId || !redirectUri) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="max-w-xl w-full">
          <div className="p-8">
            <Alert variant="soft">
              <H3>Authorization error</H3>
              <Text>
                Invalid authorization request. Missing required parameters.
              </Text>
            </Alert>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <Card className="max-w-xl w-full">
        <div className="p-8">
          <div className="text-center mb-8">
            <H1>Application wants to access your Payaca account</H1>
            <Text>
              This application is requesting permission to access your account.
            </Text>

            {me?.user && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <Text>
                  <strong>Signed in as:</strong> {me.user.fullName}
                </Text>
                <div className="mt-1">
                  <Text>{me.user.account?.name}</Text>
                </div>
              </div>
            )}
          </div>

          {scopeList.length > 0 && (
            <div className="mb-6">
              <H3>This will allow the application to:</H3>
              <ul className="space-y-2">
                {scopeList.map((scopeItem, index) => (
                  <li key={index} className="flex items-start">
                    <UntitledIcon
                      name="check-circle.3"
                      className="w-5 h-5 text-green-500 mt-0.5 mr-2 flex-shrink-0"
                    />
                    <Text as="span">
                      {scopeDescriptions[scopeItem] || scopeItem}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-3">
            <Button
              onClick={handleApprove}
              disabled={approveMutation.isPending || denyMutation.isPending}
              isProcessing={approveMutation.isPending}
              className="w-full"
            >
              Authorize
            </Button>
            <Button
              variant="secondary"
              onClick={handleDeny}
              disabled={approveMutation.isPending || denyMutation.isPending}
              isProcessing={denyMutation.isPending}
              className="w-full"
            >
              Deny
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

const scopeDescriptions: Record<string, string> = {
  'admin:full_access': 'Full administrative access to your Payaca account',
} as const;

export default OAuth2AuthorizePage;
